import {
  GetAuth,
  SignIn,
  SignOut,
  SetVerifyEmailSent,
  SetUserJustSignedUp,
} from './auth';
import { SetSelectedPlanSlugAction } from './plans';
import {
  SetSubscription,
  CheckingActiveSubscription,
  SetSubscriptionCancelled,
} from './subscription';
import {
  UpdateWorkoutPlayer,
  SkipExercise,
  PreviousExercise,
  CancelWorkout,
  EndWorkout,
  PlayExerciseIndex,
  ToggleShowPlaylist,
  PauseWorkout,
  SwWorkoutReady,
  LoadWorkout,
  ToggleShowPreviewPanel,
} from './workout-player';
import {
  SetExerciseFilters,
  SearchExercises,
  ClearExerciseFilters,
} from './exercises';
import { SetCustomEditWorkouts } from './custom-workouts';
import {
  SetEditWorkout,
  RemoveEditWorkoutExercise,
  ResetEditWorkoutState,
  SetEditWorkoutAdvancedTiming,
  SetEditWorkoutAdvancedTimingAtIndex,
  EditWorkoutAddExercises,
  EditHydratedWorkout,
  SetEditWorkoutCategory,
} from './edit-workout';
import {
  SetUserStats,
  GetUserStats,
  SetUserLocale,
  UserProfilePhotoUploading,
  UserProfilePhotoUploaded,
  SetUserAccount,
  SetUserDoc,
  SetUserIntegrations,
  IncreaseSessionCount,
  SetFavorites,
} from './user';
import {
  SetPendingNotifications,
  SetInactiveDays,
  SetInactivityReminderTime,
  SetDailyReminderTime,
  SetNotificationsEnabled,
  SetNotificationsPermission,
  SetNextWorkoutReminderDate,
  CancelNotificationById,
} from './notifications';

import {
  SetMute,
  SetVolume,
  SetAudioDucking,
  SetMusicPlaylists,
  SetMySworkit,
  SetVoiceCommands,
  DismissPtDisclaimer,
  DismissMSKDisclaimer,
  IncreaseLaunchCount,
} from './local-settings';
import {
  SetPaymentPlans,
  SetPaymentError,
  SetPaymentPlanLoadingError,
  GetPaymentPlans,
  SetReferralCodeError,
  SetReferralCodeSuccess,
  SetPaymentSuccess,
} from './purchase';
import { SetRouteDetail } from './router';
import {
  SyncFilesStarted,
  SyncFilesCompleted,
  SyncFilesStatus,
  SyncFilesError,
} from './file-sync';
import { SetPhotoEndWorkout } from './photo';
import {
  MusicPlay,
  MusicUpdateState,
  MusicPause,
  MusicVolume,
  MusicSkip,
  MusicSetFeedFMStations,
  MusicChangeStation,
  MusicInit,
  MusicStop,
} from './music-player';
import {
  ToggleMotivationalMessages,
  ToggleProductAnnouncements,
  PushNotificationsPromptSeen,
} from './push-notifications';
import { GlobalModalShown } from './global-modal';
import { ToggleOnline } from './app';
import { SetRemoteConfigKey } from './experiments';
import {
  SetDeployStatus,
  CheckForUpdate,
  UpdateAvailable,
} from './deploy-update';
import {
  SetStripeCoupon,
  SetCouponCodeError,
  SetCouponCodeSuccess,
} from './purchase-stripe';
import {
  SetWorkoutFilters,
  SearchWorkouts,
  ClearWorkoutFilters,
} from './workouts';
import {
  GetAssignedMembers,
  SetOrg,
  SetOrgAssignments,
  UpdateAssignment,
} from './portal';
import { SetActiveChallengeBanner } from './challenge-banner';
import { GetUserAssignmentData, GetUserAssignments } from './user-assignments';

export interface NullAction {
  type: TypeKeys.NULL;
}

// Keep this type updated with each known action
export type ActionTypes =
  | CancelNotificationById
  | CancelWorkout
  | CheckForUpdate
  | CheckingActiveSubscription
  | ClearExerciseFilters
  | ClearWorkoutFilters
  | DismissPtDisclaimer
  | DismissMSKDisclaimer
  | EditHydratedWorkout
  | EditWorkoutAddExercises
  | EndWorkout
  | GetAuth
  | GetPaymentPlans
  | GetUserStats
  | GlobalModalShown
  | IncreaseLaunchCount
  | IncreaseSessionCount
  | LoadWorkout
  | MusicChangeStation
  | MusicInit
  | MusicVolume
  | MusicPause
  | MusicPlay
  | MusicSetFeedFMStations
  | MusicSkip
  | MusicStop
  | MusicUpdateState
  | NullAction
  | PauseWorkout
  | PlayExerciseIndex
  | PreviousExercise
  | PushNotificationsPromptSeen
  | RemoveEditWorkoutExercise
  | ResetEditWorkoutState
  | SearchExercises
  | SearchWorkouts
  | SetActiveChallengeBanner
  | SetAudioDucking
  | SetCouponCodeError
  | SetCouponCodeSuccess
  | SetCustomEditWorkouts
  | SetDailyReminderTime
  | SetDeployStatus
  | SetEditWorkout
  | SetEditWorkoutAdvancedTiming
  | SetEditWorkoutCategory
  | SetEditWorkoutAdvancedTimingAtIndex
  | SetExerciseFilters
  | SetFavorites
  | SetInactiveDays
  | SetInactivityReminderTime
  | SetMusicPlaylists
  | SetMute
  | SetMySworkit
  | SetNextWorkoutReminderDate
  | SetNotificationsEnabled
  | SetNotificationsPermission
  | SetOrg
  | SetPaymentError
  | SetPaymentPlanLoadingError
  | SetPaymentPlans
  | SetPaymentSuccess
  | SetPendingNotifications
  | SetPhotoEndWorkout
  | SetReferralCodeError
  | SetReferralCodeSuccess
  | SetRemoteConfigKey
  | SetRouteDetail
  | SetSelectedPlanSlugAction
  | SetStripeCoupon
  | SetSubscription
  | SetSubscriptionCancelled
  | SetUserAccount
  | SetUserDoc
  | SetUserIntegrations
  | SetUserJustSignedUp
  | SetUserLocale
  | SetUserStats
  | SetVerifyEmailSent
  | SetVoiceCommands
  | SetVolume
  | SetWorkoutFilters
  | SignIn
  | SignOut
  | SkipExercise
  | SwWorkoutReady
  | SyncFilesError
  | SyncFilesStarted
  | SyncFilesStatus
  | SyncFilesCompleted
  | ToggleMotivationalMessages
  | ToggleOnline
  | ToggleProductAnnouncements
  | ToggleShowPlaylist
  | ToggleShowPreviewPanel
  | UpdateAvailable
  | UpdateWorkoutPlayer
  | UserProfilePhotoUploading
  | UserProfilePhotoUploaded
  | UpdateAssignment
  | GetUserAssignments
  | GetAssignedMembers
  | SetOrgAssignments
  | GetUserAssignmentData;

export enum TypeKeys {
  CANCEL_NOTIFICATION_BY_ID = 'CANCEL_NOTIFICATION_BY_ID',
  CANCEL_WORKOUT = 'CANCEL_WORKOUT',
  CHECK_FOR_UPDATE = 'CHECK_FOR_UPDATE',
  CHECKING_ACTIVE_SUBSCRIPTION = 'CHECKING_ACTIVE_SUBSCRIPTION',
  CLEAR_EXERCISE_FILTERS = 'CLEAR_EXERCISE_FILTERS',
  CLEAR_WORKOUT_FILTERS = 'CLEAR_WORKOUT_FILTERS',
  DISMISS_PT_DISCLAIMER = 'DISMISS_PT_DISCLAIMER',
  DISMISS_MSK_DISCLAIMER = 'DISMISS_MSK_DISCLAIMER',
  EDIT_HYDRATED_WORKOUT = 'EDIT_HYDRATED_WORKOUT',
  EDIT_WORKOUT_ADD_EXERCISES = 'EDIT_WORKOUT_ADD_EXERCISES',
  END_WORKOUT = 'END_WORKOUT',
  GET_AUTH = 'GET_AUTH',
  GET_CUSTOM_WORKOUTS = 'GET_CUSTOM_WORKOUTS',
  GET_PAYMENT_PLANS = 'GET_PAYMENT_PLANS',
  GET_USER_STATS = 'GET_USER_STATS',
  GLOBAL_MODAL_SHOWN = 'GLOBAL_MODAL_SHOWN',
  HEALTH_WRITE_AUTHORIZED = 'HEALTH_WRITE_AUTHORIZED',
  HEALTH_READ_AUTHORIZED = 'HEALTH_READ_AUTHORIZED',
  HEALTH_WRITE_ENABLED = 'HEALTH_WRITE_ENABLED',
  HEALTH_READ_ENABLED = 'HEALTH_READ_ENABLED',
  HEALTH_AVAILABLE = 'HEALTH_AVAILABLE',
  INCREASE_LAUNCH_COUNT = 'INCREASE_LAUNCH_COUNT',
  INCREASE_SESSION_COUNT = 'INCREASE_SESSION_COUNT',
  LOAD_WORKOUT = 'LOAD_WORKOUT',
  MUSIC_CHANGE_STATION = 'MUSIC_CHANGE_STATION',
  MUSIC_INIT = 'MUSIC_INIT',
  MUSIC_PAUSE = 'MUSIC_PAUSE',
  MUSIC_PLAY = 'MUSIC_PLAY',
  MUSIC_SKIP = 'MUSIC_SKIP',
  MUSIC_STOP = 'MUSIC_STOP',
  MUSIC_VOLUME = 'MUSIC_VOLUME',
  MUSIC_SET_FEEDFM_STATIONS = 'MUSIC_SET_FEEDFM_STATIONS',
  MUSIC_UPDATE_STATE = 'MUSIC_UPDATE_STATE',
  NEW_FEATURES_MODAL_SHOW = 'NEW_FEATURES_MODAL_SHOW',
  NEW_FEATURES_MODAL_DISMISS = 'NEW_FEATURES_MODAL_DISMISS',
  NULL = 'NULL', // Won't match anything
  PAUSE_WORKOUT = 'PAUSE_WORKOUT',
  PLAY_EXERCISE_INDEX = 'PLAY_EXERCISE_INDEX',
  PUSH_NOTIFICATIONS_PROMPT_SEEN = 'PUSH_NOTIFICATIONS_PROMPT_SEEN',
  PREVIOUS_EXERCISE = 'PREVIOUS_EXERCISE',
  REMOVE_EDIT_WORKOUT_EXERCISE = 'REMOVE_EDIT_WORKOUT_EXERCISE',
  RESET_EDIT_WORKOUT_STATE = 'RESET_EDIT_WORKOUT_STATE',
  SEARCH_EXERCISES = 'SEARCH_EXERCISES',
  SEARCH_WORKOUTS = 'SEARCH_WORKOUTS',
  SET_ACTIVE_CHALLENGE_BANNER = 'SET_ACTIVE_CHALLENGE_BANNER',
  SET_AUDIO_DUCKING = 'SET_AUDIO_DUCKING',
  SET_COUPON_CODE_ERROR = 'SET_COUPON_CODE_ERROR',
  SET_COUPON_CODE_SUCCESS = 'SET_COUPON_CODE_SUCCESS',
  SET_CURRENT_WORKOUT = 'SET_CURRENT_WORKOUT',
  SET_CUSTOM_EDIT_WORKOUTS = 'SET_CUSTOM_EDIT_WORKOUTS',
  SET_DAILY_REMINDER_TIME = 'SET_DAILY_REMINDER_TIME',
  SET_DEPLOY_STATUS = 'SET_DEPLOY_STATUS',
  SET_EDIT_WORKOUT = 'SET_EDIT_WORKOUT',
  SET_EDIT_WORKOUT_ADVANCED_TIMING = 'SET_EDIT_WORKOUT_ADVANCED_TIMING',
  SET_EDIT_WORKOUT_ADVANCED_TIMING_AT_INDEX = 'SET_EDIT_WORKOUT_ADVANCED_TIMING_AT_INDEX',
  SET_EDIT_WORKOUT_CATEGORY = 'SET_EDIT_WORKOUT_CATEGORY',
  SET_EDIT_WORKOUT_EXERCISES = 'SET_EDIT_WORKOUT_EXERCISES',
  SET_EXERCISE_FILTERS = 'SET_EXERCISE_FILTERS',
  SET_FAVORITES = 'SET_FAVORITES',
  SET_INACTIVE_DAYS = 'SET_INACTIVE_DAYS',
  SET_INACTIVE_REMINDER_TIME = 'SET_INACTIVE_REMINDER_TIME',
  SET_MUSIC_PLAYLISTS = 'SET_MUSIC_PLAYLISTS',
  SET_MUTE = 'SET_MUTE',
  SET_MY_SWORKIT = 'SET_MY_SWORKIT',
  SET_NEW_RELEASE_TO_BADGE = 'SET_NEW_RELEASE_TO_BADGE',
  SET_NEXT_WORKOUT_REMINDER_DATE = 'SET_NEXT_WORKOUT_REMINDER_DATE',
  SET_NOTIFICATIONS_ENABLED = 'SET_NOTIFICATIONS_ENABLED',
  SET_NOTIFICATIONS_PERMISSION = 'SET_NOTIFICATIONS_PERMISSION',
  SET_ORG = 'SET_ORG',
  SET_PAYMENT_ERROR = 'SET_PAYMENT_ERROR',
  SET_PAYMENT_PLAN_LOADING_ERROR = 'SET_PAYMENT_PLAN_LOADING_ERROR',
  SET_PAYMENT_PLANS = 'SET_PAYMENT_PLANS',
  SET_PAYMENT_SUCCESS = 'SET_PAYMENT_SUCCESS',
  SET_PENDING_NOTIFICATIONS = 'SET_PENDING_NOTIFICATIONS',
  SET_PHOTO_END_WORKOUT = 'SET_PHOTO_END_WORKOUT',
  SET_HEALTH_WORKOUTS = 'SET_HEALTH_WORKOUTS',
  SET_REFERRAL_CODE_SUCCESS = 'SET_REFERRAL_CODE_SUCCESS',
  SET_REFERRAL_CODE_ERROR = 'SET_REFERRAL_CODE_ERROR',
  SET_REMOTE_CONFIG_KEY = 'SET_REMOTE_CONFIG_KEY',
  SET_ROUTE_DETAIL = 'SET_ROUTE_DETAIL',
  SET_SELECTED_PLAN_SLUG = 'SET_SELECTED_PLAN_SLUG',
  SET_SUBSCRIPTION = 'SET_SUBSCRIPTION',
  SET_SUBSCRIPTION_CANCELLED = 'SET_SUBSCRIPTION_CANCELLED',
  SET_STRIPE_COUPON = 'SET_STRIPE_COUPON',
  SET_USER_ACCOUNT = 'SET_USER_ACCOUNT',
  SET_USER_DOC = 'SET_USER_DOC',
  SET_USER_INTEGRATIONS = 'SET_USER_INTEGRATIONS',
  SET_USER_JUST_SIGNED_UP = 'SET_USER_JUST_SIGNED_UP',
  SET_USER_LOCALE = 'SET_USER_LOCALE',
  SET_USER_PROFILE = 'SET_USER_PROFILE',
  SET_USER_STATS = 'SET_USER_STATS',
  SET_VERIFY_EMAIL_SENT = 'SET_VERIFY_EMAIL_SENT',
  SET_VOICE_COMMANDS = 'SET_VOICE_COMMANDS',
  SET_VOLUME = 'SET_VOLUME',
  SET_WORKOUT_FILTERS = 'SET_WORKOUT_FILTERS',
  SIGN_IN = 'SIGN_IN',
  SIGN_OUT = 'SIGN_OUT',
  SKIP_EXERCISE = 'SKIP_EXERCISE',
  SW_WORKOUT_READY = 'SW_WORKOUT_READY',
  SYNC_FILES_ERROR = 'SYNC_FILES_ERROR',
  SYNC_FILES_STARTED = 'SYNC_FILES_STARTED',
  SYNC_FILES_COMPLETED = 'SYNC_FILES_COMPLETED',
  SYNC_FILES_STATUS = 'SYNC_FILES_STATUS',
  TOGGLE_MOTIVATIONAL_MESSAGES = 'TOGGLE_MOTIVATIONAL_MESSAGES',
  TOGGLE_ONLINE = 'TOGGLE_ONLINE',
  TOGGLE_PRODUCT_ANNOUNCEMENTS = 'TOGGLE_PRODUCT_ANNOUNCEMENTS',
  TOGGLE_SHOW_PLAYLIST = 'TOGGLE_SHOW_PLAYLIST',
  TOGGLE_SHOW_PREVIEW_PANEL = 'TOGGLE_SHOW_PREVIEW_PANEL',
  UPDATE_AVAILABLE = 'UPDATE_AVAILABLE',
  UPDATE_WORKOUT_PLAYER = 'UPDATE_WORKOUT_PLAYER',
  USER_PROFILE_PHOTO_UPLOADING = 'USER_PROFILE_PHOTO_UPLOADING',
  USER_PROFILE_PHOTO_UPLOADED = 'USER_PROFILE_PHOTO_UPLOADED',
  SET_SELECTED_ASSIGNMENT = 'SET_SELECTED_ASSIGNMENT',
  SET_ORG_ASSIGNMENTS = 'SET_ORG_ASSIGNMENTS',
  UPDATE_ASSIGNMENT = 'UPDATE_ASSIGNMENT',
  GET_USER_ASSIGNMENTS = 'GET_USER_ASSIGNMENTS',
  GET_ASSIGNED_MEMBERS = 'GET_ASSIGNED_MEMBERS',
  SET_ASSIGNED_MEMBERS = 'SET_ASSIGNED_MEMBERS',
  GET_USER_ASSIGNMENT_DATA = 'GET_USER_ASSIGNMENT_DATA',
}
